import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post, del } from '../lib/api.js';

interface PlatformHealth {
  platform: string;
  display_name: string;
  status: string;
  org_name?: string;
  last_sync_at?: string;
  error_message?: string;
}

interface SummaryResponse {
  data: {
    events_24h: number;
    events_by_severity: Record<string, number>;
    events_by_platform: Record<string, number>;
    events_by_type: Record<string, number>;
    unresolved_count: number;
    top_risky_users: Array<{ email: string; risk_score: number; event_count: number; platforms: string[] }>;
    trend: Array<{ date: string; critical: number; high: number; medium: number; low: number }>;
    connected_platforms: PlatformHealth[];
  };
}

interface ConnectionsResponse {
  data: {
    connections: Array<{ id: string; platform: string; display_name: string; status: string; org_name?: string; last_sync_at?: string }>;
    available_platforms: Array<{ platform: string; display_name: string; connected: boolean; scopes: string[] }>;
  };
}

const PLATFORM_ICONS: Record<string, string> = {
  google_workspace: '🔵',
  slack: '💬',
  dropbox: '📦',
  salesforce: '☁️',
};

export function CasbDashboardPage() {
  const { data: summaryData, loading, error, refresh } = useApi<SummaryResponse>('/protect/casb/summary');
  const { data: connectionsData, refresh: refreshConnections } = useApi<ConnectionsResponse>('/protect/casb/connections');
  const [connectLoading, setConnectLoading] = useState<string | null>(null);

  const summary = summaryData?.data;
  const connections = connectionsData?.data;

  async function handleConnect(platform: string) {
    setConnectLoading(platform);
    try {
      const res = await post<{ data: { auth_url: string } }>('/protect/casb/connections', { platform });
      window.open(res.data.auth_url, '_blank');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to initiate connection');
    } finally {
      setConnectLoading(null);
    }
  }

  async function handleDisconnect(connectionId: string) {
    if (!confirm('Disconnect this platform? Events will be preserved.')) return;
    try {
      await del(`/protect/casb/connections/${connectionId}`);
      refreshConnections();
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect');
    }
  }

  async function handleSync(connectionId: string) {
    try {
      await post(`/protect/casb/connections/${connectionId}/sync`);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed');
    }
  }

  function statusBadge(status: string) {
    const colors =
      status === 'active' ? 'bg-emerald-100 text-emerald-700' :
      status === 'error' ? 'bg-red-100 text-red-700' :
      status === 'needs_reauth' ? 'bg-yellow-100 text-yellow-700' :
      'bg-gray-100 text-gray-600';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>{status.replace('_', ' ')}</span>;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading CASB dashboard...</div></div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">CASB Dashboard</h2>
      <p className="text-gray-600 mb-6">Cloud Access Security Broker — monitor SaaS platforms for security events.</p>

      {/* Connected Platforms */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Connected Platforms</h3>
        <div className="grid grid-cols-4 gap-4">
          {(connections?.available_platforms ?? []).map((p) => {
            const conn = connections?.connections.find(c => c.platform === p.platform);
            return (
              <div key={p.platform} className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">{PLATFORM_ICONS[p.platform] ?? '🔌'}</span>
                  <span className="font-medium text-gray-900">{p.display_name}</span>
                </div>
                {p.connected && conn ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {statusBadge(conn.status)}
                      {conn.org_name && <span className="text-xs text-gray-500">{conn.org_name}</span>}
                    </div>
                    <p className="text-xs text-gray-400 mb-2">
                      {conn.last_sync_at ? `Last sync: ${new Date(conn.last_sync_at).toLocaleString()}` : 'Never synced'}
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => handleSync(conn.id)} className="text-xs text-blue-600 hover:text-blue-800">Sync Now</button>
                      <button onClick={() => handleDisconnect(conn.id)} className="text-xs text-red-600 hover:text-red-800">Disconnect</button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => handleConnect(p.platform)}
                    disabled={connectLoading === p.platform}
                    className="mt-2 px-3 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {connectLoading === p.platform ? 'Connecting...' : 'Connect'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Events (24h)</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary?.events_24h ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Critical</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{summary?.events_by_severity?.critical ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">High</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">{summary?.events_by_severity?.high ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Unresolved</p>
          <p className="text-2xl font-bold text-yellow-600 mt-1">{summary?.unresolved_count ?? 0}</p>
        </div>
      </div>

      {/* Top Risky Users */}
      {(summary?.top_risky_users?.length ?? 0) > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Top Risky Users</h3>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Risk Score</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Events</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Platforms</th>
                </tr>
              </thead>
              <tbody>
                {summary!.top_risky_users.map((u) => (
                  <tr key={u.email} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`font-bold ${u.risk_score >= 10 ? 'text-red-600' : u.risk_score >= 5 ? 'text-orange-600' : 'text-gray-600'}`}>{u.risk_score}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{u.event_count}</td>
                    <td className="px-4 py-3 text-gray-500">{u.platforms.map(p => PLATFORM_ICONS[p] ?? p).join(' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Event Trend (simple text list) */}
      {(summary?.trend?.length ?? 0) > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Event Trend (30 days)</h3>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="grid grid-cols-7 gap-1">
              {summary!.trend.slice(-14).map((d) => {
                const total = d.critical + d.high + d.medium + d.low;
                const bg = d.critical > 0 ? 'bg-red-400' : d.high > 0 ? 'bg-orange-300' : total > 0 ? 'bg-yellow-200' : 'bg-gray-100';
                return (
                  <div key={d.date} className={`${bg} rounded p-2 text-center`} title={`${d.date}: ${total} events`}>
                    <p className="text-xs text-gray-600">{d.date.slice(5)}</p>
                    <p className="text-sm font-bold">{total}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
